/**
 * ATS Users Tab Component
 * User management table with bulk actions, edit, delete functionality
 */

import { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import {
    RefreshCw,
    Loader2,
    MoreHorizontal,
    Pencil,
    Trash2,
    UserMinus,
    UserPlus,
    FileDown,
    Copy,
    ExternalLink
} from 'lucide-react';
import ATSEditUserDialog from './ATSEditUserDialog';
import ATSDeleteUserDialog from './ATSDeleteUserDialog';

const ATSUsersTab = ({ api }) => {
    const [atsUsers, setAtsUsers] = useState([]);
    const [atsStats, setAtsStats] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    
    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const fetchAtsUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const [usersRes, statsRes] = await Promise.all([
                api.get('/ats/users?active_only=false'),
                api.get('/ats/users/stats')
            ]);
            setAtsUsers(usersRes.data);
            setAtsStats(statsRes.data);
        } catch (error) {
            toast.error('Failed to load ATS users');
        } finally {
            setLoadingUsers(false);
        }
    }, [api]);

    useEffect(() => {
        fetchAtsUsers();
    }, [fetchAtsUsers]);

    const getBaseUrl = () => {
        return process.env.REACT_APP_BACKEND_URL || window.location.origin;
    };

    const getSsoUrl = (ssoToken) => {
        if (!ssoToken) return null;
        return `${getBaseUrl()}/ats-login?ssotoken=${ssoToken}`;
    };

    const openSsoLink = (ssoToken) => {
        const url = getSsoUrl(ssoToken);
        if (url) {
            window.open(url, '_blank');
        } else {
            toast.error('No SSO token available for this user');
        }
    };

    const copySsoLink = (ssoToken) => {
        const url = getSsoUrl(ssoToken);
        if (url) {
            navigator.clipboard.writeText(url);
            toast.success('SSO link copied to clipboard');
        } else {
            toast.error('No SSO token available for this user');
        }
    };

    // Selection handlers
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedUsers(atsUsers.map(u => u.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId, checked) => {
        if (checked) {
            setSelectedUsers(prev => [...prev, userId]);
        } else {
            setSelectedUsers(prev => prev.filter(id => id !== userId));
        }
    };

    const isAllSelected = atsUsers.length > 0 && selectedUsers.length === atsUsers.length;
    const isPartiallySelected = selectedUsers.length > 0 && selectedUsers.length < atsUsers.length;

    // Bulk actions
    const handleBulkMarkInactive = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }
        
        setBulkActionLoading(true);
        try {
            const response = await api.post('/ats/users/bulk-inactive', { user_ids: selectedUsers });
            toast.success(`${response.data.summary.deactivated_count} users marked as inactive`);
            setSelectedUsers([]);
            fetchAtsUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to mark users as inactive');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }
        
        if (!window.confirm(`Are you sure you want to permanently delete ${selectedUsers.length} user(s)? This action cannot be undone.`)) {
            return;
        }
        
        setBulkActionLoading(true);
        try {
            const response = await api.post('/ats/users/bulk-delete', { user_ids: selectedUsers });
            toast.success(`${response.data.summary.deleted_count} users deleted`);
            setSelectedUsers([]);
            fetchAtsUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete users');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const reactivateUser = async (userId) => {
        try {
            await api.post(`/ats/users/${userId}/reactivate`);
            toast.success('User reactivated');
            fetchAtsUsers();
        } catch (error) {
            toast.error('Failed to reactivate user');
        }
    };

    // Export to Excel
    const handleExportToExcel = async () => {
        setExportLoading(true);
        try {
            const response = await api.get('/ats/users/export');
            const data = response.data.data;
            
            if (!data || data.length === 0) {
                toast.error('No users to export');
                return;
            }
            
            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(data);
            
            // Set column widths
            ws['!cols'] = [
                { wch: 25 },  // name
                { wch: 30 },  // email
                { wch: 12 },  // tek_id
                { wch: 15 },  // department
                { wch: 15 },  // location
                { wch: 10 },  // shift
                { wch: 15 },  // ats_role
                { wch: 12 },  // experience
                { wch: 10 },  // status
                { wch: 60 },  // sso_url
                { wch: 20 },  // created_at
                { wch: 20 }   // last_synced
            ];
            
            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'ATS Users');
            
            // Generate file and download
            XLSX.writeFile(wb, `ats_users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            toast.success(`Exported ${data.length} users to Excel`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to export users');
        } finally {
            setExportLoading(false);
        }
    };

    // Edit user
    const openEditDialog = (user) => {
        setEditingUser(user);
        setEditDialogOpen(true);
    };

    const handleUserUpdated = () => {
        setEditDialogOpen(false);
        setEditingUser(null);
        fetchAtsUsers();
    };

    // Delete user
    const openDeleteDialog = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleUserDeleted = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        fetchAtsUsers();
    };

    if (loadingUsers) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Stats */}
            {atsStats && (
                <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border text-center">
                        <p className="text-3xl font-bold">{atsStats.total}</p>
                        <p className="text-sm text-muted-foreground">Total ATS Users</p>
                    </div>
                    <div className="p-4 rounded-lg border text-center bg-emerald-500/5">
                        <p className="text-3xl font-bold text-emerald-600">{atsStats.active}</p>
                        <p className="text-sm text-muted-foreground">Active</p>
                    </div>
                    <div className="p-4 rounded-lg border text-center bg-amber-500/5">
                        <p className="text-3xl font-bold text-amber-600">{atsStats.deactivated}</p>
                        <p className="text-sm text-muted-foreground">Deactivated</p>
                    </div>
                </div>
            )}

            {/* Bulk Actions Bar */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                        {selectedUsers.length > 0 
                            ? `${selectedUsers.length} user(s) selected` 
                            : 'Select users for bulk actions'}
                    </span>
                    {selectedUsers.length > 0 && (
                        <div className="flex items-center gap-2">
                            <Button 
                                size="sm" 
                                variant="outline"
                                onClick={handleBulkMarkInactive}
                                disabled={bulkActionLoading}
                                data-testid="ats-bulk-inactive-btn"
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                    <UserMinus className="h-3 w-3 mr-1" />
                                )}
                                Mark Inactive
                            </Button>
                            <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={handleBulkDelete}
                                disabled={bulkActionLoading}
                                data-testid="ats-bulk-delete-btn"
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                    <Trash2 className="h-3 w-3 mr-1" />
                                )}
                                Delete Selected
                            </Button>
                        </div>
                    )}
                </div>
                <Button 
                    variant="outline" 
                    onClick={handleExportToExcel}
                    disabled={exportLoading || atsUsers.length === 0}
                    data-testid="ats-export-excel-btn"
                >
                    {exportLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <FileDown className="h-4 w-4 mr-2" />
                    )}
                    Export to Excel
                </Button>
            </div>

            {/* Users Table */}
            <div className="rounded-lg border">
                <ScrollArea className="h-[400px]">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={isAllSelected}
                                        onCheckedChange={handleSelectAll}
                                        aria-label="Select all users"
                                        className={isPartiallySelected ? "data-[state=checked]:bg-primary/50" : ""}
                                        data-testid="ats-select-all-checkbox"
                                    />
                                </TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Tek ID</TableHead>
                                <TableHead>Department</TableHead>
                                <TableHead>Location</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-24">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {atsUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        No ATS users found. Import users or sync from your ATS.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                atsUsers.map((user) => (
                                    <TableRow key={user.id} data-testid={`ats-user-row-${user.id}`}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedUsers.includes(user.id)}
                                                onCheckedChange={(checked) => handleSelectUser(user.id, checked)}
                                                aria-label={`Select ${user.name}`}
                                                data-testid={`ats-user-checkbox-${user.id}`}
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">{user.name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.tek_id || '-'}</TableCell>
                                        <TableCell>{user.department || '-'}</TableCell>
                                        <TableCell>{user.location || '-'}</TableCell>
                                        <TableCell>
                                            {user.ats_active ? (
                                                <Badge className="bg-emerald-500/10 text-emerald-600">Active</Badge>
                                            ) : (
                                                <Badge className="bg-amber-500/10 text-amber-600">Inactive</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" data-testid={`ats-user-actions-${user.id}`}>
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openEditDialog(user)} data-testid={`ats-edit-user-${user.id}`}>
                                                        <Pencil className="h-4 w-4 mr-2" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => copySsoLink(user.sso_token)} data-testid={`ats-copy-sso-${user.id}`}>
                                                        <Copy className="h-4 w-4 mr-2" />
                                                        Copy SSO Link
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => openSsoLink(user.sso_token)} data-testid={`ats-open-sso-${user.id}`}>
                                                        <ExternalLink className="h-4 w-4 mr-2" />
                                                        Open SSO Link
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    {!user.ats_active && (
                                                        <DropdownMenuItem onClick={() => reactivateUser(user.id)}>
                                                            <UserPlus className="h-4 w-4 mr-2" />
                                                            Reactivate
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem 
                                                        onClick={() => openDeleteDialog(user)}
                                                        className="text-red-600 focus:text-red-600"
                                                        data-testid={`ats-delete-user-${user.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </ScrollArea>
            </div>

            <Button variant="outline" onClick={fetchAtsUsers} data-testid="ats-refresh-users-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
            </Button>

            {/* Edit User Dialog */}
            <ATSEditUserDialog
                api={api}
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                user={editingUser}
                onSuccess={handleUserUpdated}
            />

            {/* Delete User Dialog */}
            <ATSDeleteUserDialog
                api={api}
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                user={userToDelete}
                onSuccess={handleUserDeleted}
            />
        </div>
    );
};

export default ATSUsersTab;
